import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import styled from "styled-components";
import FontStyles from "../styles/fontStyles";
import {
  createTheme,
  FormControl,
  ThemeProvider,
} from "@mui/material";
import { ptBR } from "date-fns/locale";
import { Colors, fonts, Spacing } from "../../config";
import { FieldProps, useFormFull } from "form-full";
import { validations } from "../../utils";
import colors from "../../config/colors";

export interface TextInputDateBaseProps extends FieldProps<any> {
  label?: string;
  value?: any;
  alternativeColors?: boolean;
  inputStyle?: any;
  required?: string;
  readOnly?: boolean;
  icon?: any;
  theme?: any;
  disableError?: boolean;
  borderRadius?: any;
  variant?: "desktop" | "mobile" | "static";
  views?: Array<"year" | "month" | "day">;
  autoOk?: boolean;
  format?: string;
  minDate?: any;
  maxDate?: any;
  autoComplete?: string;
  end?: string;
  restProps?: any;
  name: string;
  defaultValue?: any;
  onChange?: any;
  validation?: any;
}

const customTheme = createTheme({
  components: {
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          marginInline: "0px !important",
          marginTop: 3,
        },
      },
    },
    MuiPickersInputBase: {
      styleOverrides: {
        root: {
          padding: "23px",
          backgroundColor: Colors.inputGray,
          borderRadius: 5,
          height: 51,
          fontFamily: fonts.medium,
          fontSize: fonts.sizes.regular,
          "&:hover": {
            backgroundColor: Colors.disabled,
          },
          "&::before": {
            borderBottom: "none !important",
          },
          "&::after": {
            borderBottom: "none !important",
          },
        },
       
      },
    },
    MuiPickersFilledInput: {
      styleOverrides: {
        sectionsContainer: {
          padding: 0,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: Colors.black,
        },
      },
    },
    MuiPickersToolbar: {
      styleOverrides: {
        root: {
          backgroundColor: colors.black,
          borderBottom: "1px solid #24272A",
          [`@media (max-width:${581 - 1}px)`]: {
            height: Spacing(6.25),
          },
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          fontFamily: fonts.regular,
        },
      },
    },
    MuiPickersDay: {
      styleOverrides: {
        root: {
          color: colors.black,
          "&.Mui-selected": {
            backgroundColor: colors.gray,
            color: colors.whiteBackground,
            "&:hover": {
              backgroundColor: colors.black + "E6",
              color: "white",
              border: "1px solid #24272A",
            },
          },
          "&.MuiPickersDay-today": {
            color: "#3f51b5",
          },
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          backgroundColor: colors.black,
          [`@media (max-width:${581 - 1}px)`]: {
            padding: Spacing(0, 1, 1, 0),
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          minWidth: "32px",
          color: colors.whiteBackground,
          fontFamily: fonts.bold,
        },
      },
    },
    MuiYearCalendar: {
      styleOverrides: {
        root: {
          fontFamily: fonts.regular,
          "&.Mui-selected": {
            color: colors.black,
          },
        },
      },
    },
    MuiMonthCalendar: {
      styleOverrides: {
        root: {
          "&.Mui-selected": {
            color: colors.black,
          },
        },
      },
    },
    MuiPickersCalendarHeader: {
      styleOverrides: {
        root: {
          [`@media (max-width:${581 - 1}px)`]: {
            marginInline: Spacing(1.5),
          },
        },
      },
    },
    MuiDateCalendar: {
      styleOverrides: {
        root: {
          backgroundColor: colors.inputGray,
        },
      },
    },
  },
});

const Label = styled.p<{ $witherror?: boolean }>(({ theme, $witherror }) => {
  const { palette: colors } = theme as any;
  return {
    ...FontStyles.semibold14,
    textTransform: "uppercase",
    color: $witherror ? colors.error.main : colors.action.selected,
    transition: ".2s",
    pointerEvents: "none",
    marginTop: 0,
  };
});

function InputDateComponent(props: TextInputDateBaseProps) {
  const { value, error, onBlur, onChange, ref, ffHandler } = useFormFull.field({
    validation: (data: any) =>
      validations.inputDate(data, props.maxDate, props.minDate),
    ...props,
  });

  const {
    label,
    required,
    disableError,
    views = ["year", "month", "day"],
    format,
    readOnly,
    minDate,
    maxDate,
  } = props;

  return (
    <FormControl fullWidth error={Boolean(error)}>
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
        <Label $witherror={Boolean(error) ? true : undefined}>
          {label}
          {Boolean(required)}
        </Label>
        <ThemeProvider theme={customTheme}>
          <DatePicker
            format={format ? format : "dd/MM/yyyy"}
            minDate={
              typeof minDate !== "string" && minDate !== null
                ? minDate
                : undefined
            }
            maxDate={
              typeof maxDate !== "string" && maxDate !== null
                ? maxDate
                : undefined
            }
            value={value ? value : null}
            views={views}
            inputRef={ref}
            readOnly={readOnly}
            onChange={(date) => {
              if (!readOnly) {
                onChange(null, date);
                setTimeout(() => {
                  onBlur(ffHandler?.testFieldError(props.name));
                }, 10);
              }
            }}
            slotProps={{
              textField: {
                variant: "filled",
                fullWidth: true,
                required: Boolean(required),
                error: Boolean(error),
                helperText: disableError ? null : error ? error : " ",
                InputProps: {
                  disableUnderline: true,
                  readOnly: readOnly,
                },
              },
              openPickerButton: {
                sx: readOnly ? { pointerEvents: "none", color: "rgba(0, 0, 0, 0.54)" } : {},
              },
            }}
          />
        </ThemeProvider>
      </LocalizationProvider>
    </FormControl>
  );
}

export default InputDateComponent;
